import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import { DialogService } from '@mode/shared/ui';
import { ShareDialogComponent } from './share-dialog.component';
import { ShareDialogData } from './share-dialog-data.interface';
import { ShareDialogEmailForm } from './email-tab/share-dialog-email-form.interface';
import { ShareDialogSlackForm } from './slack-tab/slack-tab.interface';

@Injectable({
  providedIn: 'root',
})
export class ShareDialogService {
  private readonly id = 'mode-share-dialog-modal';
  private readonly width = '462px';
  private readonly minHeight = '520px';
  private readonly maxHeight = '596px';

  constructor(private _dialogService: DialogService) {}

  public open({
    data$,
    sendingEmail$,
    toggleExternalShareAction,
    sendEmail,
    scheduleDelivery,
    sendSlackMessage,
  }: {
    data$: Observable<ShareDialogData>;
    sendingEmail$: Observable<boolean>;
    toggleExternalShareAction: (state: boolean) => void;
    sendEmail: (formData: ShareDialogEmailForm) => void;
    scheduleDelivery: () => void;
    sendSlackMessage: (formData: ShareDialogSlackForm) => void;
  }) {
    const destroy$ = new Subject<void>();
    const dialogExists = this._dialogService.getDialogById(this.id);
    if (!dialogExists) {
      const shareDialogRef = this._dialogService.open({
        component: ShareDialogComponent,
        matDialogConfig: {
          id: this.id,
          width: this.width,
          minHeight: this.minHeight,
          maxHeight: this.maxHeight,
          data: {
            data$,
            sendingEmail$,
          },
        },
      });

      shareDialogRef.componentInstance.toggleExternalShare.pipe(takeUntil(destroy$)).subscribe((state) => {
        if (toggleExternalShareAction) {
          toggleExternalShareAction(state);
        }
      });

      shareDialogRef.componentInstance.sendEmail.pipe(takeUntil(destroy$)).subscribe((formData) => {
        if (sendEmail) {
          sendEmail(formData);
        }
      });

      shareDialogRef.componentInstance.sendSlackMessage.pipe(takeUntil(destroy$)).subscribe((formData) => {
        if (sendSlackMessage) {
          sendSlackMessage(formData);
        }
      });

      shareDialogRef.componentInstance.scheduleDelivery.pipe(takeUntil(destroy$)).subscribe(() => {
        if (scheduleDelivery) {
          scheduleDelivery();
        }
      });

      shareDialogRef.componentInstance.cancel.pipe(takeUntil(destroy$)).subscribe(() => {
        this.close();
      });

      return shareDialogRef.afterClosed().pipe(
        tap(() => {
          destroy$.next();
          destroy$.complete();
        })
      );
    }
  }

  public close(): void {
    this._dialogService.closeAll();
  }
}
